import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { 
  ArrowLeft, 
  Plus, 
  ScrollText,
  Download,
} from "lucide-react"
import { User, Earth, WandSparkles } from "lucide-react"
import { LoadingSpinner } from "@/components/ui/loading-spinner"
import { TarotCard } from "@/components/cards/templates/TarotLayout"
import { MTGCard } from "@/components/cards/templates/MTGLayout"
import { UFOCard } from "@/components/cards/templates/UFOLayout"
import { FlyerCard } from "@/components/cards/templates/FlyerLayout"
import { BaseCard } from "@/components/cards/templates/BaseLayout"
import { DeckData } from "@/types/decks"
import { UFOCardData } from "@/components/cards/templates/UFOLayout"
import { MTGCardData } from "@/components/cards/templates/MTGLayout"
import { TarotCardData } from "@/components/cards/templates/TarotLayout"
import { FlyerCardData } from "@/components/cards/templates/FlyerLayout"
import { generateCard, getDeck } from "@/services/api"
import { useBreakpoint } from "@/providers/BreakpointProvider"
import { cn } from "@/lib/utils/general"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

interface DeckDetailProps {
  deck: DeckData | null;
  loading: boolean;
}

const renderCard = (card: DeckData['cards'][0]) => {
  // Ensure metadata is parsed
  const metadata = typeof card.metadata === 'string' ? 
    JSON.parse(card.metadata) : card.metadata;

  const universe = card.universe?.toLowerCase() || '';
  
  // Prepare card data with consistent structure
  const cardData = {
    id: card.id,
    title: card.title,
    description: card.description,
    imageUrl: card.imageUrl,
    theme: card.universe || 'Default',
    metadata: {
      ...metadata,
      cardType: metadata?.cardType || metadata?.type,
      artStyle: metadata?.artStyle || 'default'
    },
    creator: {
      id: card.creator?.id || 0,
      username: card.creator?.username || 'Anonymous'
    },
    stats: card.stats
  };

  switch (universe) {
    case 'fantasy':
      return <MTGCard cardData={cardData as MTGCardData} />;
    case 'tarot':
      return <TarotCard cardData={cardData as TarotCardData} />;
    case 'ufo':
      return <UFOCard cardData={cardData as UFOCardData} />;
    case 'flyers':
      return <FlyerCard cardData={cardData as FlyerCardData} />;
    default:
      return <BaseCard cardData={cardData} />;
  }
};

export function DeckDetail({ deck, loading }: DeckDetailProps) {
  const navigate = useNavigate()
  const { isMobile } = useBreakpoint()
  const [showCardTypeDialog, setShowCardTypeDialog] = useState(false)
  const [isCreatingCard, setIsCreatingCard] = useState(false)
  const [deckData, setDeckData] = useState<DeckData | null>(null)
  const [api, setApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)
  const [fullscreenCard, setFullscreenCard] = useState<number | null>(null);

  // Move all useEffect hooks to the top level
  useEffect(() => {
    console.log('DeckDetail props:', { deck, loading });
    if (deck) {
      console.log('Setting deck data:', deck);
      setDeckData(deck);
    }
  }, [deck]);

  useEffect(() => {
    if (!api) return;

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap())
    })
  }, [api]);

  // Always render the escape key handler, but only active when needed
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && fullscreenCard !== null) {
        setFullscreenCard(null);
      }
    };
    
    if (fullscreenCard !== null) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [fullscreenCard]);

  const handleCreateCardClick = () => {
    setShowCardTypeDialog(true)
  }

  const handleCreateCard = async (cardType: string) => {
    if (!deck) return
    
    setIsCreatingCard(true)
    try {
      const response = await generateCard({
        themeId: deck.theme_id,
        fields: {
          type: cardType,
          artStyle: deck.metadata?.artStyle || 'standard',
          colorScheme: 'default',
          illustration: 'default'
        },
        language: 'en',
        playerName: 'Player'
      })
      
      console.log('Card creation response:', response)
      
      if (response?.data) {
        setShowCardTypeDialog(false)
        // Refresh immediately after card creation
        try {
          const refreshResponse = await getDeck(deck.id.toString())
          console.log('Refresh response:', refreshResponse)
          
          // Handle the nested data structure
          const newDeckData = refreshResponse?.data?.data || refreshResponse?.data
          if (newDeckData) {
            console.log('Setting new deck data:', newDeckData)
            setDeckData(newDeckData)
          }
        } catch (refreshError) {
          console.error('Error refreshing deck:', refreshError)
        }
      }
    } catch (error) {
      console.error('Error creating card:', error)
    } finally {
      setIsCreatingCard(false)
    }
  }

  // Add this new component for the shimmer effect
  const CardWithShimmer = ({ card, onClick }: { card: DeckData['cards'][0], onClick?: () => void }) => {
    const [showShimmer, setShowShimmer] = useState(false);

    const handleClick = () => {
      setShowShimmer(true);
      setTimeout(() => setShowShimmer(false), 1000);
      onClick?.();
    };

    return (
      <div 
        className="relative cursor-pointer"
        onClick={handleClick}
      >
        <div className="relative">
          {renderCard(card)}
          {showShimmer && (
            <div 
              className={cn(
                "absolute inset-0 pointer-events-none overflow-hidden",
                "before:absolute before:inset-0",
                "before:bg-gradient-to-r before:from-transparent before:via-white/60 before:to-transparent",
                "before:translate-x-[-100%]",
                "before:animate-[shimmer_1s_ease-in-out_forwards]"
              )}
            />
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    console.log('Rendering loading state');
    return (
      <div className="w-full h-full">
        <div className="w-full max-w-7xl mx-auto px-4">
          {/* Keep the header visible during loading */}
          <div className="flex items-center justify-between mt-0">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/decks')}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <h1 className="text-lg font-bold">Loading...</h1>
            </div>
          </div>
          
          <div className="flex justify-center items-center min-h-[50vh]">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  if (!deckData) {
    console.log('No deck data available');
    return (
      <div className="w-full h-full">
        <div className="w-full max-w-7xl mx-auto px-4">
          {/* Keep the header visible when no data */}
          <div className="flex items-center justify-between mt-0">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/decks')}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <h1 className="text-lg font-bold">Deck not found</h1>
            </div>
          </div>
          
          <div className="flex justify-center items-center min-h-[50vh]">
            <p className="text-muted-foreground">No deck found</p>
          </div>
        </div>
      </div>
    );
  }

  console.log('Rendering deck:', deckData);

  return (
    <>
      <div className={cn(
        "w-full h-full transition-opacity duration-700",
        fullscreenCard !== null && "opacity-30 pointer-events-none"
      )}>
        <div className="w-full max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="flex items-center justify-between mt-0">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/decks')}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <h1 className="text-lg font-bold">{deckData?.theme_title}</h1>
            </div>
            
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCreateCardClick}
                className="hover:bg-accent"
              >
                <Plus className="h-5 w-5 text-foreground" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="hover:bg-accent"
              >
                <ScrollText className="h-5 w-5 text-foreground" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="hover:bg-accent"
              >
                <Download className="h-5 w-5 text-foreground" />
              </Button>
            </div>
          </div>

          {/* Cards Section */}
          <div className="mt-4">
            {!deckData.cards.length ? (
              <div className="flex flex-col items-center justify-center p-8 text-center rounded-md border bg-card">
                <p className="text-muted-foreground">No cards in this deck</p>
              </div>
            ) : (
              <Carousel
                setApi={setApi}
                opts={{
                  align: "start",
                  loop: true,
                  slidesToScroll: 1,
                  containScroll: false,
                  dragFree: false
                }}
                className="w-full"
              >
                <CarouselContent className="gap-6">
                  {deckData.cards.map((card) => (
                    <CarouselItem
                      key={card.id}
                      className={cn(
                        "flex items-center justify-center",
                        isMobile ? "basis-[100%]" : "basis-1/3",
                        "min-w-0"
                      )}
                    >
                      <div className="w-full flex justify-center">
                        <CardWithShimmer 
                          card={card}
                          onClick={() => {}}
                        />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>

                {/* Navigation Controls */}
                <div className={cn(
                  "mt-2 flex flex-col items-center justify-center gap-2",
                  isMobile ? "flex" : "hidden"
                )}>
                  <span className="text-sm text-muted-foreground mt-4">
                    Card {current + 1} of {deckData.cards.length}
                  </span>
                </div>

                <div className={cn(
                  isMobile ? "hidden" : "block"
                )}>
                  <CarouselPrevious className="-left-12 bg-muted text-foreground hover:bg-muted/90 border-border" />
                  <CarouselNext className="-right-12 bg-muted text-foreground hover:bg-muted/90 border-border" />
                </div>
              </Carousel>
            )}
          </div>
        </div>
      </div>

      <Dialog open={showCardTypeDialog} onOpenChange={setShowCardTypeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Choose Card Type</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4">
            {isCreatingCard ? (
              <div className="flex justify-center items-center p-8">
                <LoadingSpinner />
                <span className="ml-2">Creating card...</span>
              </div>
            ) : (
              <>
                <Button
                  onClick={() => handleCreateCard('CHARACTER')}
                  className="flex items-center p-4"
                  disabled={isCreatingCard}
                >
                  <User className="h-4 w-4 mr-2" />
                  <span>Character</span>
                </Button>
                <Button
                  onClick={() => handleCreateCard('ENVIRONMENT')}
                  className="flex items-center p-4"
                  disabled={isCreatingCard}
                >
                  <Earth className="h-4 w-4 mr-2" />
                  <span>Environment</span>
                </Button>
                <Button
                  onClick={() => handleCreateCard('POWER')}
                  className="flex items-center p-4"
                  disabled={isCreatingCard}
                >
                  <WandSparkles className="h-4 w-4 mr-2" />
                  <span>Power</span>
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(100%); }
          100% { transform: translateX(300%); }
        }
      `}} />
    </>
  )
} 